import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';

import dayjs from '~/timeInit';

import DeleteIcon from '@mui/icons-material/DeleteForever';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  GridLegacy as Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';

import DateTimeWidget from '~/components/Common/Elements/DateTimeWidget';

import useLocalizedDatePeriod from '~/hooks/useLocalizedDatePeriod';

import { getDateInUtcTimezone } from '~/services/TimeService';

import PromotionalDiscountSummary from './PromotionalDiscountSummary';
import { PromotionalDiscount, PromotionalDiscountAccordionProps } from './types';

function parseNumericValue(raw: string): number | null {
  if (raw === '') {
    return null;
  }
  const num = Number(raw);
  if (isNaN(num)) {
    return null;
  }
  return num;
}

function isValidPercentage(value: number | null): boolean {
  return value == null || (value >= 0 && value <= 100);
}

export default function PromotionalDiscountAccordion({
  index,
  value,
  onChange,
  onDelete,
}: PromotionalDiscountAccordionProps) {
  const [expanded, setExpanded] = useState(false);
  const [discountPercentError, setDiscountPercentError] = useState(false);
  const [luxPlusPercentError, setLuxPlusPercentError] = useState(false);

  const browserTimezone = useMemo(() => dayjs.tz.guess(), []);

  const schedule = value.schedules[0];

  const { local: activePeriodLocal, range: activePeriodRange } = useLocalizedDatePeriod(schedule.activePeriod);
  const { local: travelPeriodLocal, range: travelPeriodRange } = useLocalizedDatePeriod(schedule.travelPeriod);

  const updateField = useCallback(
    (patch: Partial<PromotionalDiscount>) => {
      onChange({ ...value, ...patch });
    },
    [value, onChange],
  );

  const handlePercentChange = useCallback(
    (
      event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      field: 'discount_percent' | 'luxplus_discount_percent',
    ) => {
      const raw = event.target.value;
      const setError = field === 'discount_percent' ? setDiscountPercentError : setLuxPlusPercentError;

      if (raw === '') {
        updateField({ [field]: field === 'discount_percent' ? 0 : null });
        setError(false);
        return;
      }

      const num = Number(raw);
      if (!isNaN(num)) {
        updateField({ [field]: num });
        setError(!isValidPercentage(num));
      }
    },
    [updateField],
  );

  const handleNumericChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, field: keyof PromotionalDiscount) => {
      updateField({ [field]: parseNumericValue(event.target.value) });
    },
    [updateField],
  );

  const handleDeleteClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      onDelete();
    },
    [onDelete],
  );

  const handleDateChange = useCallback(
    (periodKey: 'activePeriod' | 'travelPeriod', dateKey: 'from' | 'to', date: string) => {
      onChange({
        ...value,
        schedules: [
          {
            ...schedule,
            [periodKey]: {
              ...schedule[periodKey],
              [dateKey]: getDateInUtcTimezone(date),
            },
          },
        ],
      });
    },
    [value, schedule, onChange],
  );

  return (
    <Accordion expanded={expanded} onChange={() => setExpanded((prev) => !prev)} sx={{ bgcolor: 'grey.50' }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1 }}>
          <PromotionalDiscountSummary index={index} value={value} />
          <IconButton color="error" size="small" onClick={handleDeleteClick}>
            <DeleteIcon />
          </IconButton>
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        <Typography variant="overline" color="primary" fontWeight="bold">
          Discount Details
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Discount Name"
              placeholder="e.g. Early Bird"
              value={value.discount_name || ''}
              onChange={(e) => updateField({ discount_name: e.target.value || null })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Discount %"
              type="number"
              value={value.discount_percent}
              onChange={(e) => handlePercentChange(e, 'discount_percent')}
              error={discountPercentError}
              helperText={discountPercentError ? 'Value must be between 0 and 100' : undefined}
              inputProps={{ min: 0, max: 100 }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Lux Plus %"
              type="number"
              value={value.luxplus_discount_percent ?? ''}
              onChange={(e) => handlePercentChange(e, 'luxplus_discount_percent')}
              error={luxPlusPercentError}
              helperText={luxPlusPercentError ? 'Value must be between 0 and 100' : undefined}
              inputProps={{ min: 0, max: 100 }}
              fullWidth
            />
          </Grid>
        </Grid>

        <Typography variant="overline" color="primary" fontWeight="bold">
          Stay & Arrival Options
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Min LOS"
              type="number"
              value={value.min_los ?? ''}
              onChange={(e) => handleNumericChange(e, 'min_los')}
              inputProps={{ min: 0 }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Max LOS"
              type="number"
              value={value.max_los ?? ''}
              onChange={(e) => handleNumericChange(e, 'max_los')}
              inputProps={{ min: 0 }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Min Days to Arrival"
              type="number"
              value={value.min_days_to_arrival ?? ''}
              onChange={(e) => handleNumericChange(e, 'min_days_to_arrival')}
              inputProps={{ min: 0 }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Max Days to Arrival"
              type="number"
              value={value.max_days_to_arrival ?? ''}
              onChange={(e) => handleNumericChange(e, 'max_days_to_arrival')}
              inputProps={{ min: 0 }}
              fullWidth
            />
          </Grid>
        </Grid>

        {/* Date Periods */}
        <Typography variant="overline" color="primary" fontWeight="bold">
          Date Periods
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <DateTimeWidget
              label="Active Period From"
              format="DD-MM-YYYY"
              showDateOnly
              defaultTimezone={browserTimezone}
              value={activePeriodLocal.from}
              maxDate={activePeriodRange[1] ?? undefined}
              onChange={(date) => handleDateChange('activePeriod', 'from', date)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <DateTimeWidget
              label="Active Period To"
              format="DD-MM-YYYY"
              showDateOnly
              defaultTimezone={browserTimezone}
              value={activePeriodLocal.to}
              minDate={activePeriodRange[0] ?? undefined}
              onChange={(date) => handleDateChange('activePeriod', 'to', date)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <DateTimeWidget
              label="Travel Period From"
              format="DD-MM-YYYY"
              showDateOnly
              defaultTimezone={browserTimezone}
              value={travelPeriodLocal.from}
              maxDate={travelPeriodRange[1]?.subtract(1, 'day') ?? undefined}
              onChange={(date) => handleDateChange('travelPeriod', 'from', date)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <DateTimeWidget
              label="Travel Period To"
              format="DD-MM-YYYY"
              showDateOnly
              defaultTimezone={browserTimezone}
              value={travelPeriodLocal.to}
              minDate={travelPeriodRange[0]?.add(1, 'day') ?? undefined}
              onChange={(date) => handleDateChange('travelPeriod', 'to', date)}
            />
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
}
